import React from "react"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import styles from "../charts.module.css"
import { formatValueWithUnit } from "../chartUtils"
import { generateColorPalette } from "../../../../utils/utils"

const MAX_VISIBLE_LEGENDS = 6
const MAX_VISIBLE_SLICES = 24
const OTHERS_COLOR = "#64748b"
const CATEGORY_COLORS = [
    "#6245ae", "#d2417c", "#0a9c22", "#ff9b32", "#38d1e5", "#8b5cf6",
    "#ef4444", "#14b8a6", "#eab308", "#2563eb", "#f97316", "#22c55e",
    "#a855f7", "#06b6d4", "#f43f5e", "#84cc16", "#6366f1", "#ec4899",
    "#10b981", "#f59e0b", "#3b82f6", "#dc2626", "#7c3aed", "#0891b2"
]

const ChartDonut = ({
    data,
    colors = CATEGORY_COLORS,
    unit = "",
}) => {
    const isOthersItem = (item) => /^outr[oa]s(\s|\(|$)/i.test(`${item?.name || ""}`.trim())

    const positiveData = data.filter(item => Number(item.value || 0) > 0)
    const regularData = positiveData
        .filter(item => !isOthersItem(item))
        .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
    const existingOthersData = positiveData.filter(isOthersItem)

    const hasGroupedSlices = positiveData.length > MAX_VISIBLE_SLICES
    const visibleSliceCount = hasGroupedSlices ? MAX_VISIBLE_SLICES - 1 : MAX_VISIBLE_SLICES
    const visibleData = regularData.slice(0, visibleSliceCount)
    const groupedData = [
        ...regularData.slice(visibleSliceCount),
        ...existingOthersData,
    ]
    const groupedValue = groupedData.reduce((acc, item) => acc + Number(item.value || 0), 0)
    const chartData = groupedData.length > 0
        ? [
            ...visibleData,
            {
                name: `Outros (${groupedData.length})`,
                value: groupedValue,
                groupedItems: groupedData,
                isOthers: true,
            },
        ]
        : visibleData
    const total = chartData.reduce((sum, d) => sum + Number(d.value || 0), 0)
    const palette = generateColorPalette(colors, chartData.length, { minLightness: 34, maxLightness: 72, variationLightnessStep: 7, hueStep: 42 })
    const sliceGap = chartData.length > 12 ? 0.25 : 0.6
    const defaultSliceStrokeWidth = chartData.length > 12 ? 1 : 1.5

    if (total === 0) {
        return <div className={styles.emptyChartMessage}><p>Nenhum dado para exibir</p></div>
    }

    const getSlicePercentage = (value) => total > 0 ? (Number(value || 0) / total) * 100 : 0

    const getPercentage = (value) =>
        getSlicePercentage(value).toFixed(2) + "%"

    const getSliceStrokeWidth = (value) => {
        if (getSlicePercentage(value) < 2) return 0
        return defaultSliceStrokeWidth
    }

    // --- TOOLTIP ---
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload
            const fill = payload[0].payload.fill ?? payload[0].color

            return (
                <div className={styles.tooltip} style={{ borderLeft: `4px solid ${fill || "var(--brand-primary)"}` }}>
                    <p>
                        <span className={styles.tooltipLabel}>{item.name}</span>
                        <span className={styles.tooltipValue}>{formatValueWithUnit(item.value, unit)} ({getPercentage(item.value)})</span>
                    </p>
                </div>
            )
        }
        return null
    }

    const othersLegend = chartData.find(item => item.isOthers || isOthersItem(item))
    const regularLegends = chartData.filter(item => !(item.isOthers || isOthersItem(item)))
    const visibleRegularLegends = othersLegend
        ? regularLegends.slice(0, MAX_VISIBLE_LEGENDS - 1)
        : regularLegends.slice(0, MAX_VISIBLE_LEGENDS)
    const hiddenLegends = othersLegend
        ? regularLegends.slice(MAX_VISIBLE_LEGENDS - 1)
        : regularLegends.slice(MAX_VISIBLE_LEGENDS)
    const hiddenLegendsCount = hiddenLegends.length

    return (
        <div className={styles.pieLayout}>
            <div className={styles.pieCanvas}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Tooltip content={<CustomTooltip />} offset={12} wrapperStyle={{ outline: "none" }} />
                        <Pie
                            data={chartData}
                            dataKey="value"
                            innerRadius="58%"
                            outerRadius="82%"
                            paddingAngle={sliceGap}
                            stroke="var(--surface-card)"
                            strokeWidth={defaultSliceStrokeWidth}
                            isAnimationActive={false}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${entry.name}-${index}`}
                                    fill={entry.isOthers || isOthersItem(entry) ? OTHERS_COLOR : entry.color || palette[index % palette.length]}
                                    stroke="var(--surface-card)"
                                    strokeWidth={getSliceStrokeWidth(entry.value)}
                                />
                            ))}
                        </Pie>
                        <text
                            x="50%"
                            y="44%"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="var(--text-secondary)"
                            fontSize={11}
                            fontWeight={700}
                        >
                            Total
                        </text>
                        <text
                            x="50%"
                            y="52%"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="var(--text-primary)"
                            fontSize={20}
                            fontWeight={800}
                        >
                            {total}
                        </text>
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className={styles.compactLegend}>
                {visibleRegularLegends.map((item, index) => (
                    <div className={styles.compactLegendItem} key={`legend-${item.name}-${index}`} title={`${item.name} - ${getPercentage(item.value)}`}>
                        <span className={styles.legendDot} style={{ backgroundColor: item.isOthers || isOthersItem(item) ? OTHERS_COLOR : item.color || palette[index % palette.length] }} />
                        <span className={styles.legendName}>{item.name}</span>
                        <strong>{getPercentage(item.value)}</strong>
                    </div>
                ))}
                {hiddenLegendsCount > 0 && (
                    <div className={styles.compactLegendMore} title={hiddenLegends.map(item => `${item.name} - ${getPercentage(item.value)}`).join("\n")}>
                        +{hiddenLegendsCount} outros
                    </div>
                )}
                {othersLegend && (
                    <div className={styles.compactLegendItem} key={`legend-${othersLegend.name}-others`} title={`${othersLegend.name} - ${getPercentage(othersLegend.value)}`}>
                        <span className={styles.legendDot} style={{ backgroundColor: OTHERS_COLOR }} />
                        <span className={styles.legendName}>{othersLegend.name}</span>
                        <strong>{getPercentage(othersLegend.value)}</strong>
                    </div>
                )}
            </div>
            {hasGroupedSlices && (
                <div className={styles.chartLimitNote}>
                    Exibindo as {visibleSliceCount} maiores categorias. As demais foram agrupadas em Outros.
                </div>
            )}
        </div>
    )
}

export default ChartDonut
